import React, { useState, useEffect, useCallback } from 'react';
import { FileText, Search, Eye, CheckCircle, Clock, AlertCircle, X, Send, ChevronLeft, Edit, Save, Plus, Trash2 } from 'lucide-react';
import { supabase } from './supabaseClient';
import { InvoicePrintView } from './InvoicePrintView';

const InvoiceManagement = ({ initialSearchTerm = '' }) => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [editMode, setEditMode] = useState(false);
  const [editedInvoice, setEditedInvoice] = useState(null);
  const [invoiceItems, setInvoiceItems] = useState([]);

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('invoices')
        .select(`
          *,
          customer:customers(id, first_name, last_name, company, email, phone, address, city, province, postal_code),
          job:jobs(id, job_number, job_address, job_city, job_province, job_postal_code, start_date, completion_date, estimate_id)
        `)
        .order('invoice_date', { ascending: false });

      if (error) throw error;
      setInvoices(data || []);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      alert('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  const filterInvoices = useCallback(() => {
    let filtered = invoices;

    // Status filter
    if (statusFilter !== 'All') {
      filtered = filtered.filter(inv => inv.status === statusFilter);
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(inv => {
        const customerName = `${inv.customer?.first_name || ''} ${inv.customer?.last_name || ''} ${inv.customer?.company || ''}`.toLowerCase();
        return (
          inv.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          customerName.includes(searchTerm.toLowerCase()) ||
          inv.job?.job_number?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }

    setFilteredInvoices(filtered);
  }, [invoices, statusFilter, searchTerm]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  useEffect(() => {
    filterInvoices();
  }, [filterInvoices]);

  const fetchInvoiceItems = useCallback(async (invoiceId) => {
    try {
      const { data, error } = await supabase
        .from('invoice_items')
        .select('*')
        .eq('invoice_id', invoiceId)
        .order('sort_order');

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error fetching invoice items:', error);
      return [];
    }
  }, []);

  const handleViewInvoice = async (invoice) => {
    const items = await fetchInvoiceItems(invoice.id);
    setSelectedInvoice({ ...invoice, items });
    setInvoiceItems(items);
    setShowDetail(true);
  };

  const handleEditInvoice = async (invoice) => {
    const items = await fetchInvoiceItems(invoice.id);
    console.log('Fetched invoice items for editing:', items);

    // If no items exist, start with one empty line item
    const itemsToEdit = items.length > 0 ? items : [{ description: '', quantity: 1, unit_price: 0, amount: 0 }];

    setSelectedInvoice({ ...invoice, items });
    setEditedInvoice({ ...invoice });
    setInvoiceItems(itemsToEdit);
    setEditMode(true);
    setShowDetail(true);
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedInvoice(null);
  };

  const handleSaveInvoice = async () => {
    try {
      // Calculate total from line items
      const subtotal = invoiceItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0);
      const hst = subtotal * 0.13;
      const total = subtotal + hst;

      // Update invoice
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({
          total_amount: total,
          notes: editedInvoice.notes,
          due_date: editedInvoice.due_date,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedInvoice.id);

      if (invoiceError) throw invoiceError;

      // Delete existing items
      const { error: deleteError } = await supabase
        .from('invoice_items')
        .delete()
        .eq('invoice_id', selectedInvoice.id);

      if (deleteError) throw deleteError;

      // Insert new items
      const itemsToInsert = invoiceItems
        .filter(item => item.description && item.description.trim())
        .map((item, index) => ({
          invoice_id: selectedInvoice.id,
          description: item.description,
          quantity: parseFloat(item.quantity) || 1,
          unit_price: parseFloat(item.unit_price) || 0,
          amount: parseFloat(item.amount) || 0,
          sort_order: index
        }));

      if (itemsToInsert.length > 0) {
        const { error: insertError } = await supabase
          .from('invoice_items')
          .insert(itemsToInsert);

        if (insertError) throw insertError;
      }

      // Refresh invoices
      await fetchInvoices();

      // Update selected invoice
      const updatedInvoice = {
        ...selectedInvoice,
        ...editedInvoice,
        total_amount: total,
        items: itemsToInsert
      };
      setSelectedInvoice(updatedInvoice);
      setEditMode(false);
      setEditedInvoice(null);

      alert('Invoice updated successfully!');
    } catch (error) {
      console.error('Error saving invoice:', error);
      alert('Failed to save invoice');
    }
  };

  const handleAddLineItem = () => {
    setInvoiceItems([...invoiceItems, { description: '', quantity: 1, unit_price: 0, amount: 0 }]);
  };

  const handleRemoveLineItem = (index) => {
    const newItems = invoiceItems.filter((_, i) => i !== index);
    setInvoiceItems(newItems);
  };

  const handleLineItemChange = (index, field, value) => {
    const newItems = [...invoiceItems];
    newItems[index][field] = value;

    // Auto-calculate amount when quantity or unit_price changes
    if (field === 'quantity' || field === 'unit_price') {
      const quantity = parseFloat(newItems[index].quantity) || 0;
      const unitPrice = parseFloat(newItems[index].unit_price) || 0;
      newItems[index].amount = (quantity * unitPrice).toFixed(2);
    }

    setInvoiceItems(newItems);
  };

  const handlePrintInvoice = async (invoice) => {
    // Fetch invoice items
    const items = await fetchInvoiceItems(invoice.id);

    // If no invoice items, fallback to estimate items
    let itemsToUse = items;
    if (items.length === 0 && invoice.job?.estimate_id) {
      const { data, error } = await supabase
        .from('estimate_items')
        .select('*')
        .eq('estimate_id', invoice.job.estimate_id)
        .order('sort_order');

      if (!error && data) {
        itemsToUse = data.map(item => ({
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unit_price,
          amount: item.amount
        }));
      }
    }

    setSelectedInvoice({ ...invoice, items: itemsToUse });
    setShowPrintView(true);
  };

  const handleUpdateStatus = async (invoiceId, newStatus) => {
    try {
      console.log('Updating invoice:', invoiceId, 'to status:', newStatus);

      const updateData = {
        status: newStatus
      };

      console.log('Update data:', updateData);

      const { data, error } = await supabase
        .from('invoices')
        .update(updateData)
        .eq('id', invoiceId)
        .select();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Update successful:', data);

      // Update selected invoice if viewing
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({ ...selectedInvoice, ...updateData });
      }

      // Refresh the invoices list
      await fetchInvoices();

      alert(`Invoice status updated to ${newStatus}`);
    } catch (error) {
      console.error('Error updating invoice status:', error);
      alert(`Failed to update invoice status: ${error.message || error}`);
    }
  };

  const handleDeleteInvoice = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('invoices')
        .delete()
        .eq('id', invoiceId);

      if (error) throw error;

      await fetchInvoices();
      setShowDetail(false);
      setSelectedInvoice(null);
    } catch (error) {
      console.error('Error deleting invoice:', error);
      alert('Failed to delete invoice');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Paid':
        return <CheckCircle className="text-green-600" size={20} />;
      case 'Sent':
        return <Send className="text-blue-600" size={20} />;
      case 'Overdue':
        return <AlertCircle className="text-red-600" size={20} />;
      case 'Cancelled':
        return <X className="text-gray-600" size={20} />;
      default:
        return <Clock className="text-yellow-600" size={20} />;
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Paid':
        return 'bg-green-100 text-green-800';
      case 'Sent':
        return 'bg-blue-100 text-blue-800';
      case 'Overdue':
        return 'bg-red-100 text-red-800';
      case 'Cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD'
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-CA');
  };

  const isOverdue = (invoice) => {
    if (invoice.status === 'Paid' || invoice.status === 'Cancelled') return false;
    const dueDate = new Date(invoice.due_date);
    const today = new Date();
    return dueDate < today;
  };

  // Calculate summary statistics (based on filtered list so customer/search filters are reflected)
  const stats = {
    total: filteredInvoices.length,
    draft: filteredInvoices.filter(inv => inv.status === 'Draft').length,
    sent: filteredInvoices.filter(inv => inv.status === 'Sent').length,
    overdue: filteredInvoices.filter(inv => isOverdue(inv)).length,
    paid: filteredInvoices.filter(inv => inv.status === 'Paid').length,
    totalOutstanding: filteredInvoices
      .filter(inv => inv.status !== 'Paid' && inv.status !== 'Cancelled')
      .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0),
    totalPaid: filteredInvoices
      .filter(inv => inv.status === 'Paid')
      .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0)
  };

  if (showPrintView && selectedInvoice) {
    return (
      <InvoicePrintView
        invoice={selectedInvoice}
        onClose={() => {
          setShowPrintView(false);
          setSelectedInvoice(null);
        }}
      />
    );
  }

  if (showDetail && selectedInvoice) {
    return (
      <div className="p-4 sm:p-6">
        <div>
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={() => {
                setShowDetail(false);
                setSelectedInvoice(null);
                setEditMode(false);
                setEditedInvoice(null);
              }}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeft size={20} />
              Back to Invoices
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {selectedInvoice.invoice_number}
                </h2>
                <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mt-2 ${getStatusBadgeColor(selectedInvoice.status)}`}>
                  {selectedInvoice.status}
                </span>
              </div>
              <div className="flex gap-2">
                {editMode ? (
                  <>
                    <button
                      onClick={handleSaveInvoice}
                      className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Save size={20} />
                      Save Changes
                    </button>
                    <button
                      onClick={handleCancelEdit}
                      className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg"
                    >
                      Cancel
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => handleEditInvoice(selectedInvoice)}
                      className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <Edit size={20} />
                      Edit Invoice
                    </button>
                    <button
                      onClick={() => handlePrintInvoice(selectedInvoice)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
                    >
                      <FileText size={20} />
                      Print Invoice
                    </button>
                    <button
                      onClick={() => handleDeleteInvoice(selectedInvoice.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6 mb-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Customer</h3>
                <p className="text-lg font-semibold">
                  {selectedInvoice.customer?.first_name} {selectedInvoice.customer?.last_name}
                  {selectedInvoice.customer?.company && ` (${selectedInvoice.customer.company})`}
                </p>
                <p className="text-gray-600">{selectedInvoice.customer?.email}</p>
                <p className="text-gray-600">{selectedInvoice.customer?.phone}</p>
                <p className="text-gray-600 mt-2">
                  {selectedInvoice.customer?.address}<br />
                  {selectedInvoice.customer?.city}, {selectedInvoice.customer?.province} {selectedInvoice.customer?.postal_code}
                </p>
              </div>

              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Job Details</h3>
                <p className="text-gray-800"><span className="font-medium">Job #:</span> {selectedInvoice.job?.job_number}</p>
                <p className="text-gray-800"><span className="font-medium">Location:</span></p>
                <p className="text-gray-600">
                  {selectedInvoice.job?.job_address}<br />
                  {selectedInvoice.job?.job_city}, {selectedInvoice.job?.job_province} {selectedInvoice.job?.job_postal_code}
                </p>
                <p className="text-gray-800 mt-2">
                  <span className="font-medium">Completed:</span> {formatDate(selectedInvoice.job?.completion_date)}
                </p>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Invoice Date</h3>
                  <p className="text-gray-900">{formatDate(selectedInvoice.invoice_date)}</p>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-1">Due Date</h3>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedInvoice.due_date}
                      onChange={(e) => setEditedInvoice({ ...editedInvoice, due_date: e.target.value })}
                      className="border border-gray-300 rounded px-3 py-1"
                    />
                  ) : (
                    <p className="text-gray-900">{formatDate(selectedInvoice.due_date)}</p>
                  )}
                </div>
              </div>

              {editMode && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                  <div className="space-y-2">
                    {invoiceItems.map((item, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 items-center bg-gray-50 p-3 rounded">
                        <div className="col-span-5">
                          <input
                            type="text"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="Qty"
                            value={item.quantity}
                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="Unit Price"
                            value={item.unit_price}
                            onChange={(e) => handleLineItemChange(index, 'unit_price', e.target.value)}
                            className="w-full border border-gray-300 rounded px-3 py-2"
                            step="0.01"
                          />
                        </div>
                        <div className="col-span-2">
                          <input
                            type="number"
                            placeholder="Amount"
                            value={item.amount}
                            readOnly
                            className="w-full border border-gray-300 rounded px-3 py-2 bg-gray-100"
                          />
                        </div>
                        <div className="col-span-1 flex justify-center">
                          <button
                            onClick={() => handleRemoveLineItem(index)}
                            className="text-red-600 hover:text-red-800"
                            title="Remove line item"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={handleAddLineItem}
                    className="mt-3 flex items-center gap-2 text-blue-600 hover:text-blue-800"
                  >
                    <Plus size={18} />
                    Add Line Item
                  </button>

                  <div className="mt-6 bg-gray-50 p-4 rounded-lg">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="text-gray-700">Subtotal:</span>
                        <span className="font-medium">{formatCurrency(invoiceItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0))}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-700">HST (13%):</span>
                        <span className="font-medium">{formatCurrency(invoiceItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) * 0.13)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-lg font-semibold text-gray-900">Total:</span>
                        <span className="text-lg font-bold text-gray-900">{formatCurrency(invoiceItems.reduce((sum, item) => sum + parseFloat(item.amount || 0), 0) * 1.13)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {!editMode && invoiceItems.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Description</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Quantity</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Unit Price</th>
                          <th className="px-4 py-2 text-right text-sm font-medium text-gray-700">Amount</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {invoiceItems.map((item, index) => (
                          <tr key={index}>
                            <td className="px-4 py-2 text-gray-900">{item.description}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{item.quantity}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{formatCurrency(item.unit_price)}</td>
                            <td className="px-4 py-2 text-right text-gray-900">{formatCurrency(item.amount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-lg font-medium text-gray-700">Total Amount</span>
                  <span className="text-2xl font-bold text-gray-900">{formatCurrency(selectedInvoice.total_amount)}</span>
                </div>
              </div>
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Notes</h3>
              {editMode ? (
                <textarea
                  value={editedInvoice.notes || ''}
                  onChange={(e) => setEditedInvoice({ ...editedInvoice, notes: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 min-h-24"
                  placeholder="Add notes or payment terms..."
                />
              ) : (
                <p className="text-gray-700 whitespace-pre-wrap">{selectedInvoice.notes || 'No notes'}</p>
              )}
            </div>

            <div className="border-t pt-6 mt-6">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Update Status</h3>
              <div className="flex gap-2">
                <button
                  onClick={() => handleUpdateStatus(selectedInvoice.id, 'Draft')}
                  className={`px-4 py-2 rounded-lg ${selectedInvoice.status === 'Draft' ? 'bg-yellow-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Draft
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedInvoice.id, 'Sent')}
                  className={`px-4 py-2 rounded-lg ${selectedInvoice.status === 'Sent' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Sent
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedInvoice.id, 'Paid')}
                  className={`px-4 py-2 rounded-lg ${selectedInvoice.status === 'Paid' ? 'bg-green-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Mark as Paid
                </button>
                <button
                  onClick={() => handleUpdateStatus(selectedInvoice.id, 'Cancelled')}
                  className={`px-4 py-2 rounded-lg ${selectedInvoice.status === 'Cancelled' ? 'bg-gray-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <FileText size={32} />
            Invoice Management
          </h1>
          <p className="text-gray-600 mt-2">Manage customer invoices and track payments</p>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Total Invoices</p>
            <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Outstanding</p>
            <p className="text-2xl font-bold text-orange-600">{formatCurrency(stats.totalOutstanding)}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.sent + stats.overdue} invoices</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Overdue</p>
            <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <p className="text-sm text-gray-600">Paid</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(stats.totalPaid)}</p>
            <p className="text-xs text-gray-500 mt-1">{stats.paid} invoices</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Search by invoice #, customer, or job #..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="flex gap-2">
              {['All', 'Draft', 'Sent', 'Overdue', 'Paid', 'Cancelled'].map(status => (
                <button
                  key={status}
                  onClick={() => setStatusFilter(status)}
                  className={`px-4 py-2 rounded-lg ${
                    statusFilter === status
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Invoice List */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-gray-500">Loading invoices...</div>
          ) : filteredInvoices.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No invoices found. Invoices are generated from completed jobs.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Invoice #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job #</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredInvoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{invoice.invoice_number}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-gray-900">
                          {invoice.customer?.first_name} {invoice.customer?.last_name}
                          {invoice.customer?.company && ` (${invoice.customer.company})`}
                        </div>
                        <div className="text-sm text-gray-500">{invoice.customer?.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {invoice.job?.job_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-900">
                        {formatDate(invoice.invoice_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className={isOverdue(invoice) ? 'text-red-600 font-medium' : 'text-gray-900'}>
                          {formatDate(invoice.due_date)}
                        </div>
                        {isOverdue(invoice) && (
                          <div className="text-xs text-red-600">OVERDUE</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">
                        {formatCurrency(invoice.total_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(invoice.status)}
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(invoice.status)}`}>
                            {invoice.status}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleViewInvoice(invoice)}
                            className="text-blue-600 hover:text-blue-800"
                            title="View Details"
                          >
                            <Eye size={18} />
                          </button>
                          <button
                            onClick={() => handleEditInvoice(invoice)}
                            className="text-orange-600 hover:text-orange-800"
                            title="Edit Invoice"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handlePrintInvoice(invoice)}
                            className="text-green-600 hover:text-green-800"
                            title="Print Invoice"
                          >
                            <FileText size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceManagement;
